/**
 * GRI Mega Blaster Landing Page
 * File: assets/gri-mb-landing.js
 *
 * Used by: sections/gri-mega-blaster.liquid
 *
 * STANDALONE: This file does NOT depend on gri-landing-page.js.
 * The Mega Blaster section should load ONLY this file. Removing
 * gri-landing-page.js from the Mega Blaster Liquid is required.
 *
 * SCOPING: Every query in this file is scoped to elements inside
 * `.gri-mb-wrapper`. This file does NOT touch the cannon-bundle
 * landing page (which has its own gri-landing-page.js). They can
 * never collide even if both sections render on the same page.
 *
 * iOS SAFARI HARDENING: variant IDs are trimmed and validated before
 * any fetch() call. Safari throws "The string did not match the
 * expected pattern" when fetch bodies contain NaN or invalid numbers.
 *
 * CART INTEGRATION: After a successful /cart/add.js, we manually
 * fetch ?section_id=cart-drawer and replaceWith() the items, footer,
 * empty-state markup, and is-empty classes — covering all five
 * elements that change between empty and populated cart states.
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────
     SCOPING HELPERS
     Find every Mega-Blaster wrapper on the page so we can attach
     listeners scoped to each one.
  ───────────────────────────────────────────── */
  function getMBSections() {
    var wrappers = document.querySelectorAll('.gri-mb-wrapper');
    var out = [];
    for (var i = 0; i < wrappers.length; i++) {
      var inner = wrappers[i].querySelector('.landed-lp[data-section-id]');
      if (inner) out.push(inner);
    }
    return out;
  }

  function qs(sel, ctx)  { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function showToast(msg) {
    var t = document.querySelector('.gri-toast');
    if (!t) {
      t = document.createElement('div');
      t.className = 'gri-toast';
      document.body.appendChild(t);
    }
    t.textContent = msg;
    t.classList.add('is-visible');
    clearTimeout(t._timer);
    t._timer = setTimeout(function () { t.classList.remove('is-visible'); }, 2800);
  }

  /* ─────────────────────────────────────────────
     VARIANT ID SANITISATION (iOS Safari fix)
  ───────────────────────────────────────────── */
  // iOS Safari is strict about fetch bodies. Returns a clean numeric
  // string, or null if the input isn't a valid positive integer.
  function sanitiseVariantId(raw) {
    if (raw == null) return null;
    var s = String(raw).trim();
    if (!s) return null;
    if (!/^\d+$/.test(s)) return null;
    var n = Number(s);
    if (!isFinite(n) || n <= 0) return null;
    return s;
  }

  /* ─────────────────────────────────────────────
     1. VARIANT TILE SELECTION (per section)
  ───────────────────────────────────────────── */
  function initVariantTiles(section) {
    var grid = qs('.lp-bb__tiles[id]', section);
    if (!grid) return;

    var tiles = qsa('.lp-bb__tile[data-i]', grid);
    tiles.forEach(function (tile) {
      tile.addEventListener('click', function () {
        tiles.forEach(function (t) {
          t.classList.remove('is-selected');
          t.setAttribute('aria-checked', 'false');
        });
        tile.classList.add('is-selected');
        tile.setAttribute('aria-checked', 'true');

        var variantId = sanitiseVariantId(tile.getAttribute('data-variant-id'));
        if (variantId) {
          // Update only THIS section's CTA buttons (and its sticky bar).
          qsa('.gri-atc-btn', section).forEach(function (btn) {
            btn.setAttribute('data-variant-id', variantId);
          });
          var sectionId = section.getAttribute('data-section-id');
          if (sectionId) {
            var stickyCta = document.querySelector(
              '#gri-sticky-' + sectionId + ' .lp-sticky__cta'
            );
            if (stickyCta) stickyCta.setAttribute('data-variant-id', variantId);
          }
        }
      });
    });
  }

  /* ─────────────────────────────────────────────
     2. ADD TO CART (per section)
  ───────────────────────────────────────────── */
  function initAddToCart(section) {
    var sectionId = section.getAttribute('data-section-id');
    var inSection = qsa('.gri-atc-btn', section);
    var stickyBtns = sectionId
      ? qsa('#gri-sticky-' + sectionId + ' .gri-atc-btn')
      : [];
    var btns = inSection.concat(stickyBtns);

    btns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var selectedTile = qs('.lp-bb__tile.is-selected[data-variant-id]', section);
        var raw = selectedTile && selectedTile.getAttribute('data-variant-id');
        if (!raw) raw = btn.getAttribute('data-variant-id');

        var variantId = sanitiseVariantId(raw);

        if (variantId) {
          addToCart(btn, variantId);
          return;
        }

        var handle = btn.getAttribute('data-product-handle');
        if (handle) {
          fetch('/products/' + encodeURIComponent(handle) + '.js', {
            credentials: 'same-origin'
          })
            .then(function (r) { return r.json(); })
            .then(function (p) {
              var first = p.variants && p.variants.find(function (v) { return v.available; });
              if (!first) first = p.variants && p.variants[0];
              if (first) {
                var fallbackId = sanitiseVariantId(first.id);
                if (fallbackId) addToCart(btn, fallbackId);
                else window.location.href = '/products/' + handle;
              } else {
                window.location.href = '/products/' + handle;
              }
            })
            .catch(function () { window.location.href = '/products/' + handle; });
          return;
        }

        showToast('Please choose a colour first.');
      });
    });
  }

  function addToCart(btn, variantId) {
    var label = btn.querySelector('.lp-fhero__buy-label') ||
                btn.querySelector('.lp-sticky__cta-label') || btn;
    var originalText = label.textContent;

    btn.setAttribute('data-loading', '1');
    btn.disabled = true;
    label.textContent = 'Adding…';

    function attempt() {
      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 })
      }).then(function (r) {
        return r.text().then(function (text) {
          var data = null;
          var isHTML = false;
          try { data = JSON.parse(text); }
          catch (e) { isHTML = /^\s*<(!DOCTYPE|html|head|body)/i.test(text); }
          return { ok: r.ok, status: r.status, data: data, raw: text, isHTML: isHTML };
        });
      });
    }

    function isConflict(res) {
      if (res.status === 429) return false;
      return res.status === 409 || res.isHTML;
    }

    function tryWithRetries(attemptsLeft, delayMs) {
      return attempt().then(function (res) {
        if (isConflict(res) && attemptsLeft > 0) {
          console.warn('[GRI-MB] Cart conflict (status ' + res.status +
                       (res.isHTML ? ', HTML body' : '') +
                       '). Retrying in ' + delayMs + 'ms.');
          return new Promise(function (resolve) {
            setTimeout(function () {
              tryWithRetries(attemptsLeft - 1, delayMs + 400).then(resolve);
            }, delayMs);
          });
        }
        return res;
      });
    }

    tryWithRetries(3, 500)
      .then(function (res) {
        if (res.status === 429) {
          throw new Error('Too many requests — please wait a moment and try again.');
        }
        if (res.isHTML) {
          throw new Error('Cart is busy — please try again in a moment.');
        }
        if (!res.ok) {
          var errMsg = (res.data && (res.data.description || res.data.message)) ||
                       ('Cart error ' + res.status);
          throw new Error(errMsg);
        }
        return res.data;
      })
      .then(function (data) {
        label.textContent = 'Added ✓';
        showToast('Added to cart! 🎉');

        return refreshCartDrawer().then(function () {
          if (typeof window.publish === 'function' &&
              window.PUB_SUB_EVENTS &&
              window.PUB_SUB_EVENTS.cartUpdate) {
            try {
              window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                source: 'gri-mb-atc',
                cartData: data,
                variantId: Number(variantId)
              });
            } catch (e) {}
          }
          openCartDrawer();
        });
      })
      .then(function () {
        setTimeout(function () {
          label.textContent = originalText;
          btn.removeAttribute('data-loading');
          btn.disabled = false;
        }, 1800);
      })
      .catch(function (err) {
        console.error('[GRI-MB] Add to cart error:', err);
        label.textContent = originalText;
        btn.removeAttribute('data-loading');
        btn.disabled = false;
        showToast(err.message || 'Something went wrong. Please try again.');
      });
  }

  /* ─────────────────────────────────────────────
     CART DRAWER REFRESH (Dawn-aware)
  ───────────────────────────────────────────── */
  function refreshCartDrawer() {
    return Promise.all([
      fetch('/?section_id=cart-drawer', {
        credentials: 'same-origin',
        headers: { 'Accept': 'text/html' }
      }).then(function (r) { return r.text(); }),
      fetch('/?section_id=cart-icon-bubble', {
        credentials: 'same-origin',
        headers: { 'Accept': 'text/html' }
      }).then(function (r) { return r.text(); })
    ]).then(function (texts) {
      var drawerHtml = new DOMParser().parseFromString(texts[0], 'text/html');
      var iconHtml   = new DOMParser().parseFromString(texts[1], 'text/html');

      var newItems = drawerHtml.querySelector('cart-drawer-items');
      var curItems = document.querySelector('cart-drawer-items');
      if (newItems && curItems) curItems.replaceWith(newItems);

      var newFooter = drawerHtml.querySelector('.cart-drawer__footer');
      var curFooter = document.querySelector('.cart-drawer__footer');
      if (newFooter && curFooter) curFooter.replaceWith(newFooter);

      var newEmpty = drawerHtml.querySelector('.drawer__inner-empty');
      var curEmpty = document.querySelector('.drawer__inner-empty');
      if (!newEmpty && curEmpty) {
        curEmpty.remove();
      } else if (newEmpty && !curEmpty) {
        var drawerInner = document.querySelector('cart-drawer .drawer__inner');
        if (drawerInner) drawerInner.insertBefore(newEmpty, drawerInner.firstChild);
      }

      var newDrawer = drawerHtml.querySelector('cart-drawer');
      var curDrawer = document.querySelector('cart-drawer');
      if (newDrawer && curDrawer) {
        curDrawer.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
      }

      var newCount = drawerHtml.querySelector('#drawer__heading-count');
      var curCount = document.querySelector('#drawer__heading-count');
      if (newCount && curCount) curCount.textContent = newCount.textContent;

      var newBubble = iconHtml.querySelector('.cart-count-bubble, [data-cart-count]');
      var curBubble = document.querySelector('.cart-count-bubble, [data-cart-count]');
      if (newBubble && curBubble) {
        curBubble.replaceWith(newBubble);
      } else if (newBubble && !curBubble) {
        var iconWrap = document.getElementById('cart-icon-bubble') ||
                       document.querySelector('a[href*="/cart"]');
        if (iconWrap) iconWrap.appendChild(newBubble);
      }
    }).catch(function (e) {
      console.warn('[GRI-MB] Drawer refresh failed:', e);
    });
  }

  function openCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    drawer.classList.remove('is-empty');
    if (typeof drawer.open === 'function') {
      try { drawer.open(); return; } catch (e) {}
    }
    drawer.classList.add('animate', 'active');
  }

  /* ─────────────────────────────────────────────
     MARQUEE (per section)
  ───────────────────────────────────────────── */
  function initMarquee(marquee, speed) {
    var offset = 0;
    var halfWidth = 0;
    var dragging = false;
    var lastX = 0;

    function measure() { halfWidth = marquee.scrollWidth / 2; }

    function tick() {
      if (!dragging) {
        offset -= speed;
        if (offset <= -halfWidth) offset += halfWidth;
      }
      marquee.style.transform = 'translateX(' + offset + 'px)';
      requestAnimationFrame(tick);
    }

    function onDown(x) { dragging = true; lastX = x; }
    function onMove(x) {
      if (!dragging) return;
      var dx = x - lastX;
      offset += dx;
      if (offset > 0) offset -= halfWidth;
      if (offset < -halfWidth) offset += halfWidth;
      lastX = x;
    }
    function onUp() { dragging = false; }

    var wrap = marquee.parentElement;
    wrap.addEventListener('mousedown', function (e) { onDown(e.clientX); });
    window.addEventListener('mousemove', function (e) { if (dragging) onMove(e.clientX); });
    window.addEventListener('mouseup', onUp);
    wrap.addEventListener('touchstart', function (e) { onDown(e.touches[0].clientX); }, { passive: true });
    wrap.addEventListener('touchmove', function (e) { if (dragging) onMove(e.touches[0].clientX); }, { passive: true });
    wrap.addEventListener('touchend', onUp);

    measure();
    window.addEventListener('resize', measure);
    requestAnimationFrame(tick);
  }

  function initUGCRail(section) {
    qsa('.lp-ugc__marquee', section).forEach(function (marquee) {
      var wrap = marquee.parentElement;
      var origCards = Array.prototype.slice.call(marquee.children);
      if (!origCards.length) return;

      origCards.forEach(function (card) {
        var clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('tabindex', '-1');
        marquee.appendChild(clone);
      });

      function autoplayAll() {
        qsa('video', marquee).forEach(function (v) {
          v.muted = true; v.play().catch(function () {});
        });
      }
      autoplayAll();

      marquee.addEventListener('click', function (e) {
        var btn = e.target.closest('.lp-ugc__play');
        if (!btn) return;
        e.stopPropagation();
        var card = btn.closest('.lp-ugc__card');
        var v = qs('video', card);
        if (!v) return;
        if (v.muted) {
          qsa('video', marquee).forEach(function (o) {
            o.muted = true; o.closest('.lp-ugc__card').classList.remove('has-sound');
          });
          v.muted = false; card.classList.add('has-sound');
        } else { v.muted = true; card.classList.remove('has-sound'); }
      });

      var wrapId = wrap.id;
      if (wrapId) {
        qsa('[data-wrap="' + wrapId + '"]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            if (btn.classList.contains('gri-ugc-pause')) wrap.classList.add('is-paused');
            else { wrap.classList.remove('is-paused'); autoplayAll(); }
          });
        });
      }

      initMarquee(marquee, 0.6);
    });
  }

  function initReviewsCarousel(section) {
    qsa('.lp-social__marquee', section).forEach(function (marquee) {
      var origCards = Array.prototype.slice.call(marquee.children);
      if (!origCards.length) return;
      origCards.forEach(function (card) {
        var clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        marquee.appendChild(clone);
      });
      initMarquee(marquee, 0.4);
    });
  }

  /* ─────────────────────────────────────────────
     STICKY BAR (per section)
  ───────────────────────────────────────────── */
  function initStickyBar(section) {
    var sectionId = section.getAttribute('data-section-id');
    if (!sectionId) return;

    var sticky = document.getElementById('gri-sticky-' + sectionId);
    if (!sticky) return;

    var heroBuyBtn = qs('.lp-fhero__buy-pill', section);
    var triggerPoint = heroBuyBtn
      ? heroBuyBtn.getBoundingClientRect().bottom + window.scrollY
      : 400;

    var chip       = qs('.lp-sticky__selected', sticky);
    var picker     = qs('.lp-sticky__picker', sticky);
    var options    = qsa('.lp-sticky__opt', sticky);
    var stickyCta  = qs('.lp-sticky__cta', sticky);
    var priceNowEl = qs('[data-sticky-price-now]', sticky);
    var priceWasEl = qs('[data-sticky-price-was]', sticky);
    var thumbEl    = qs('[data-sticky-selected-thumb]', sticky);
    var nameEl     = qs('[data-sticky-selected-name]', sticky);

    var isSyncing = false;

    function openPicker() {
      if (!picker) return;
      picker.hidden = false;
      // eslint-disable-next-line no-unused-expressions
      picker.offsetHeight;
      picker.classList.add('is-open');
      if (chip) chip.setAttribute('aria-expanded', 'true');
    }
    function closePicker() {
      if (!picker) return;
      picker.classList.remove('is-open');
      if (chip) chip.setAttribute('aria-expanded', 'false');
      setTimeout(function () {
        if (!picker.classList.contains('is-open')) picker.hidden = true;
      }, 220);
    }
    function togglePicker() {
      if (!picker) return;
      if (picker.classList.contains('is-open')) closePicker();
      else openPicker();
    }

    function onScroll() {
      if (window.scrollY > triggerPoint) sticky.classList.add('is-visible');
      else { sticky.classList.remove('is-visible'); closePicker(); }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      if (heroBuyBtn) {
        triggerPoint = heroBuyBtn.getBoundingClientRect().bottom + window.scrollY;
      }
    });
    onScroll();

    if (chip && picker) {
      chip.addEventListener('click', function (e) {
        e.stopPropagation();
        togglePicker();
      });
      document.addEventListener('click', function (e) {
        if (!picker.classList.contains('is-open')) return;
        if (e.target.closest('#gri-sticky-' + sectionId)) return;
        closePicker();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && picker.classList.contains('is-open')) {
          closePicker();
          chip.focus();
        }
      });
    }

    function applyVariant(opt, syncToMain) {
      if (!opt) return;
      options.forEach(function (o) {
        o.classList.remove('is-selected');
        o.setAttribute('aria-checked', 'false');
      });
      opt.classList.add('is-selected');
      opt.setAttribute('aria-checked', 'true');

      var rawVid = opt.getAttribute('data-variant-id');
      var vid = sanitiseVariantId(rawVid);
      var title = opt.getAttribute('data-title') || '';
      var price = opt.getAttribute('data-price') || '';
      var cmp   = opt.getAttribute('data-compare-price') || '';

      if (stickyCta && vid) stickyCta.setAttribute('data-variant-id', vid);

      if (nameEl) nameEl.textContent = title;
      if (thumbEl) {
        var optImg = qs('img', opt);
        var chipImg = qs('img', thumbEl);
        if (optImg) {
          if (chipImg) {
            chipImg.src = optImg.src;
            chipImg.alt = optImg.alt || title;
          } else {
            var newImg = document.createElement('img');
            newImg.src = optImg.src;
            newImg.alt = optImg.alt || title;
            newImg.loading = 'lazy';
            thumbEl.innerHTML = '';
            thumbEl.appendChild(newImg);
          }
        }
      }

      if (priceNowEl && price) priceNowEl.textContent = price;
      if (priceWasEl) {
        if (cmp) { priceWasEl.textContent = cmp; priceWasEl.style.display = ''; }
        else { priceWasEl.style.display = 'none'; }
      }

      if (syncToMain && !isSyncing && vid) {
        isSyncing = true;
        qsa('.lp-bb__tile[data-variant-id]', section).forEach(function (t) {
          if (t.getAttribute('data-variant-id') === vid) t.click();
        });
        setTimeout(function () { isSyncing = false; }, 100);
      }
    }

    options.forEach(function (opt) {
      opt.addEventListener('click', function () {
        applyVariant(opt, true);
        closePicker();
      });
    });

    qsa('.lp-bb__tile[data-variant-id]', section).forEach(function (tile) {
      tile.addEventListener('click', function () {
        if (isSyncing) return;
        var vid = tile.getAttribute('data-variant-id');
        if (!vid) return;
        var match = options.filter(function (o) {
          return o.getAttribute('data-variant-id') === vid;
        })[0];
        if (match) {
          isSyncing = true;
          applyVariant(match, false);
          setTimeout(function () { isSyncing = false; }, 100);
        }
      });
    });
  }

  /* ─────────────────────────────────────────────
     COLOUR TOKENS (per section)
  ───────────────────────────────────────────── */
  function injectColourTokens(section) {
    var primary = section.getAttribute('data-primary');
    var accent  = section.getAttribute('data-accent');
    if (primary) section.style.setProperty('--gri-primary', primary);
    if (accent)  section.style.setProperty('--gri-accent', accent);
  }

  /* ─────────────────────────────────────────────
     INIT (per section)
  ───────────────────────────────────────────── */
  function initSection(section) {
    if (section._griMBInited) return;
    section._griMBInited = true;

    injectColourTokens(section);
    initVariantTiles(section);
    initAddToCart(section);
    initUGCRail(section);
    initReviewsCarousel(section);
    initStickyBar(section);
  }

  function init() {
    getMBSections().forEach(initSection);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target.closest('.gri-mb-wrapper') && !e.target.querySelector('.gri-mb-wrapper')) return;
    var sec = e.target.querySelector('.gri-mb-wrapper .landed-lp[data-section-id]');
    if (sec) initSection(sec);
  });

})();